/**
 * Regras de anexos aplicadas no front (antes do envio) e na API (fonte da verdade).
 */

export const UPLOAD_POLICY = {
  maxFiles: 10,
  maxFileSizeBytes: 20 * 1024 * 1024,
  /** Extensões aceitas (minúsculas, sem ponto). */
  allowedExtensions: [
    'pdf',
    'doc',
    'docx',
    'odt',
    'rtf',
    'txt',
    'xls',
    'xlsx',
    'ods',
    'csv',
    'ppt',
    'pptx',
    'odp',
    'png',
    'jpg',
    'jpeg',
  ],
} as const;

/** MIME canônico por extensão; o servidor usa este valor em vez do informado pelo navegador. */
export const MIME_BY_EXTENSION: Record<AllowedExtension, string> = {
  pdf: 'application/pdf',
  doc: 'application/msword',
  docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  odt: 'application/vnd.oasis.opendocument.text',
  rtf: 'application/rtf',
  txt: 'text/plain',
  xls: 'application/vnd.ms-excel',
  xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  ods: 'application/vnd.oasis.opendocument.spreadsheet',
  csv: 'text/csv',
  ppt: 'application/vnd.ms-powerpoint',
  pptx: 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  odp: 'application/vnd.oasis.opendocument.presentation',
  png: 'image/png',
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
};

export type AllowedExtension = (typeof UPLOAD_POLICY.allowedExtensions)[number];

export const UPLOAD_MESSAGES = {
  tooMany: `Envie no máximo ${UPLOAD_POLICY.maxFiles} arquivos.`,
  tooLarge: `Cada arquivo pode ter no máximo ${UPLOAD_POLICY.maxFileSizeBytes / 1024 / 1024} MB.`,
  badType: `Tipo de arquivo não permitido. Aceitos: ${UPLOAD_POLICY.allowedExtensions.join(', ')}.`,
} as const;

export type UploadRejection = keyof typeof UPLOAD_MESSAGES;

export function fileExtension(fileName: string): string {
  const dot = fileName.lastIndexOf('.');
  return dot === -1 ? '' : fileName.slice(dot + 1).toLowerCase();
}

export function isAllowedFileName(fileName: string): boolean {
  return (UPLOAD_POLICY.allowedExtensions as readonly string[]).includes(fileExtension(fileName));
}

export function mimeTypeFor(fileName: string): string {
  return MIME_BY_EXTENSION[fileExtension(fileName) as AllowedExtension] ?? 'application/octet-stream';
}

/** Valida um arquivo isolado; retorna o motivo da recusa ou `null` se aceito. */
export function checkFile(file: { name: string; size: number }): UploadRejection | null {
  if (!isAllowedFileName(file.name)) return 'badType';
  if (file.size > UPLOAD_POLICY.maxFileSizeBytes) return 'tooLarge';
  return null;
}

export function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1).replace('.', ',')} MB`;
}
